import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';

import { Button } from '@/components/atoms/Buttons';
import { DataTable, type DataTableColumnDef } from '@/components/organisms/DataTable';
import type { BioscopeVideoStreamSettings, StreamQuality } from '@/domain/videoStreamSettings';
import { formatDateTimeInTimezone } from '@/utils/date';

import styles from './VideoStreamSettings.module.scss';

function formatDuration(totalSeconds: number): string {
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    return [hours, minutes, seconds]
        .map((segment) => segment.toString().padStart(2, '0'))
        .join(':');
}

function formatStreamQualityForDisplay(quality: StreamQuality, t: TFunction): string {
    return quality === 'STANDARD' ? t('internal.videoStreamSettings.qualityStandard') : 'HD';
}

interface VideoStreamSettingsTableProps {
    bioscopes: BioscopeVideoStreamSettings[];
    /** The location's IANA zone, for the last-alive time. */
    timezone: string;
    onEdit: (row: BioscopeVideoStreamSettings) => void;
}

/** One location's bioscopes with their streaming limits, each editable on its own. */
export default function VideoStreamSettingsTable(props: VideoStreamSettingsTableProps) {
    const { bioscopes, timezone, onEdit } = props;
    const { t } = useTranslation();

    const columns: DataTableColumnDef<BioscopeVideoStreamSettings>[] = [
        { id: 'cage', header: t('common.labels.cage'), cell: ({ row }) => row.original.cage.name },
        {
            id: 'bioscope',
            header: t('common.labels.bioscope'),
            cell: ({ row }) => (
                <>
                    {row.original.bioscope.id} ({row.original.bioscope.version})
                </>
            ),
        },
        {
            id: 'lastAlive',
            header: t('internal.videoStreamSettings.columns.lastAlive'),
            cell: ({ row }) =>
                row.original.bioscope.lastAlive
                    ? formatDateTimeInTimezone(row.original.bioscope.lastAlive, timezone)
                    : t('internal.client.never'),
        },
        {
            id: 'usedToday',
            header: t('internal.videoStreamSettings.columns.usedToday'),
            cell: ({ row }) => formatDuration(row.original.currentDailyUsageSeconds),
        },
        {
            id: 'maxSession',
            header: t('internal.videoStreamSettings.columns.maxSession'),
            cell: ({ row }) => formatDuration(row.original.settings.maxContinuousDurationSeconds),
        },
        {
            id: 'dailyLimit',
            header: t('internal.videoStreamSettings.columns.dailyLimit'),
            cell: ({ row }) => formatDuration(row.original.settings.dailyLimitSeconds),
        },
        {
            id: 'quality',
            header: t('internal.videoStreamSettings.columns.quality'),
            cell: ({ row }) =>
                formatStreamQualityForDisplay(row.original.settings.defaultQuality, t),
        },
        {
            id: 'actions',
            header: t('common.labels.actions'),
            cell: ({ row }) => (
                <Button variant="secondary" onClick={() => onEdit(row.original)}>
                    {t('common.actions.edit')}
                </Button>
            ),
        },
    ];

    return (
        <DataTable
            data={bioscopes}
            columns={columns}
            getRowId={(row) => `${row.cage.id}-${row.bioscope.id}`}
            enableSorting={false}
            responsive
            tableClassName={styles.settingsTable}
        />
    );
}
